import { kebabCase } from "lodash";

// Kept in step with UploadContentType on the service, which resolves the type from the
// file's own first bytes.
const EXTENSION_BY_CONTENT_TYPE: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/heic": "heic",
  "image/tiff": "tiff",
  "application/pdf": "pdf"
};

export type ReceiptSubject = {
  /** The day the expense was reported on, as `YYYY-MM-DD`. */
  date: string;
  consultantName: string;
  /** Position among the receipts of the same day, counted from one. */
  ordinal: number;
};

/**
 * The name a downloaded receipt is saved under.
 *
 * Nothing the service stores makes a usable name, so none of it is used. The consultant app
 * names the blob off the file the user picked, so every receipt is a guid; an Android gallery
 * pick with no dot in its name produced `<guid>.1000047487`, and since every stored name has
 * a dot, the token after it is as likely to be a counter as a format. When the response names
 * no file at all the browser falls back to the last path segment of the url — the attachment
 * index, a nameless, extensionless "0".
 *
 * The CRM already knows whose receipt this is and which day it belongs to, which is what
 * makes one receipt tellable from another in a Downloads folder. Only the extension has to
 * come from the response, resolved by the service from the file's own bytes; a type nothing
 * recognises leaves the receipt named but extensionless, which still beats a guid.
 */
export const receiptFileName = (
  contentType: string | undefined,
  subject: ReceiptSubject
) => {
  // kebabCase deburrs, so `Ångström` survives as `angstrom` rather than being dropped, and
  // punctuation a name may carry cannot reach the file system.
  const name = ["kvitto", subject.date, kebabCase(subject.consultantName), subject.ordinal]
    .filter((part) => part !== "")
    .join("-");

  const extension = EXTENSION_BY_CONTENT_TYPE[(contentType ?? "").split(";")[0].trim()];

  return extension ? `${name}.${extension}` : name;
};
